import { useEffect, useMemo, useState } from "react";
import { Icon, type IconName } from "@/components/Icon";
import { router } from "@/router";
import { OPENED, withCandidate } from "@/pages/Candidates/candidateLink";
import { classifyFile, formatBytes, iconForFile, isAudioFile } from "./mime";
import { useUploads, type UploadItem } from "./UploadManager";
import "./upload.css";

const LS_KEY = "newats.uploads.collapsed";

// Tray lives outside the RouterProvider tree (sibling in main.tsx Gate), so
// useNavigate() would throw at render. Use the router instance directly.
export function UploadTray() {
  const { items, cancel, retry, dismiss, clearFinished } = useUploads();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LS_KEY) === "1";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, collapsed ? "1" : "0");
    } catch {
      /* private mode */
    }
  }, [collapsed]);

  const active = items.filter((it) => it.status === "uploading" || it.status === "queued");
  const failedCount = items.filter((it) => it.status === "failed").length;
  const doneCount = items.filter((it) => it.status === "done").length;

  const totalPct = useMemo(() => {
    if (active.length === 0) return null;
    const sum = active.reduce((acc, it) => acc + (it.status === "uploading" ? it.progress : 0), 0);
    return Math.round(sum / active.length);
  }, [active]);

  if (items.length === 0) return null;

  if (collapsed) {
    return (
      <button
        type="button"
        className="up-tray up-tray--pill"
        onClick={() => setCollapsed(false)}
        title="Развернуть загрузки"
      >
        <Icon name="upload" size={14} />
        <span className="up-tray-pill-txt">
          {active.length > 0
            ? `${active.length} · ${totalPct ?? 0}%`
            : failedCount > 0
              ? `${failedCount} ошибка`
              : `${doneCount} готово`}
        </span>
        <Icon name="chevron-up" size={14} />
      </button>
    );
  }

  return (
    <div className="up-tray">
      <header className="up-tray-h">
        <div className="up-tray-title">
          <Icon name="upload" size={14} />
          <span>Загрузка файлов</span>
          {active.length > 0 && (
            <span className="up-tray-count">
              {active.length} · {totalPct ?? 0}%
            </span>
          )}
        </div>
        <div className="up-tray-h-actions">
          {(doneCount > 0 || items.some((it) => it.status === "cancelled")) && (
            <button
              type="button"
              className="up-tray-h-btn"
              onClick={clearFinished}
              title="Убрать завершённые"
            >
              Очистить
            </button>
          )}
          <button
            type="button"
            className="up-tray-h-btn up-tray-h-btn--icon"
            onClick={() => setCollapsed(true)}
            title="Свернуть"
          >
            <Icon name="minimize" size={14} />
          </button>
        </div>
      </header>
      <ul className="up-tray-list">
        {items.map((it) => (
          <Row
            key={it.id}
            it={it}
            onCancel={cancel}
            onRetry={retry}
            onDismiss={dismiss}
            onOpen={() => {
              if (it.kind === "tool-transcribe") {
                router.navigate("/tools");
                return;
              }
              const { pathname, search } = router.state.location;
              router.navigate(withCandidate(pathname, search, it.candidateId), { state: OPENED });
            }}
            openLabel={it.kind === "tool-transcribe" ? "Открыть «Инструменты»" : "Открыть карточку кандидата"}
          />
        ))}
      </ul>
    </div>
  );
}

function Row({
  it,
  onCancel,
  onRetry,
  onDismiss,
  onOpen,
  openLabel,
}: {
  it: UploadItem;
  onCancel: (id: string) => void;
  onRetry: (id: string) => void;
  onDismiss: (id: string) => void;
  onOpen: () => void;
  openLabel: string;
}) {
  const audio =
    (it.kind === "media" || it.kind === "tool-transcribe") &&
    isAudioFile(it.file.type, it.file.name);
  const iconName: IconName =
    it.kind === "media" || it.kind === "tool-transcribe"
      ? (audio ? "music" : "video")
      : iconForFile(it.file.type, it.file.name);
  const catLabel =
    it.kind === "tool-transcribe"
      ? "Расшифровка"
      : it.kind === "media"
        ? (audio ? "Аудио" : "Видео")
        : catToLabel(classifyFile(it.file.type, it.file.name));
  const size = formatBytes(it.file.size);
  const statusText = statusLine(it, size);

  const barPct =
    it.status === "done"
      ? 100
      : it.status === "uploading"
        ? it.progress
        : it.status === "failed" || it.status === "cancelled"
          ? 100
          : 0;

  return (
    <li className={`up-row up-row--${it.status}`}>
      <span className="up-row-icon">
        <Icon name={iconName} size={16} />
      </span>
      <button
        type="button"
        className="up-row-body up-row-body--link"
        onClick={onOpen}
        title={openLabel}
      >
        <div className="up-row-name">{it.file.name}</div>
        <div className="up-row-meta">
          <span>{catLabel}</span>
          <span>·</span>
          <span>{statusText}</span>
        </div>
        {it.kind === "tool-transcribe" && it.status === "done" && (
          <div className="up-row-link">Открыть «Инструменты» →</div>
        )}
        <div className={`up-row-bar up-row-bar--${it.status}`}>
          <div className="up-row-bar-fill" style={{ width: `${barPct}%` }} />
        </div>
      </button>
      <div className="up-row-actions">
        {it.status === "failed" && (
          <button
            type="button"
            className="up-row-btn"
            title="Повторить"
            onClick={() => onRetry(it.id)}
          >
            <Icon name="retry" size={14} />
          </button>
        )}
        {(it.status === "uploading" || it.status === "queued") ? (
          <button
            type="button"
            className="up-row-btn"
            title="Отменить"
            onClick={() => onCancel(it.id)}
          >
            <Icon name="close" size={14} />
          </button>
        ) : (
          <button
            type="button"
            className="up-row-btn"
            title="Убрать"
            onClick={() => onDismiss(it.id)}
          >
            <Icon name="close" size={14} />
          </button>
        )}
      </div>
    </li>
  );
}

function catToLabel(cat: ReturnType<typeof classifyFile>): string {
  switch (cat) {
    case "images":
      return "Изображение";
    case "audio":
      return "Аудио";
    case "documents":
      return "Документ";
    default:
      return "Файл";
  }
}

function statusLine(it: UploadItem, size: string): string {
  switch (it.status) {
    case "queued":
      return `в очереди · ${size}`;
    case "uploading":
      return `${it.progress}% · ${size}`;
    case "done":
      // Для инструмента «готово» означает только то, что файл долетел —
      // расшифровка продолжается на сервере, короткая ссылка снизу ведёт туда,
      // где видно её реальное состояние.
      return it.kind === "tool-transcribe" ? `отправлено · ${size}` : `готово · ${size}`;
    case "failed":
      return it.error || "ошибка";
    case "cancelled":
      return "отменено";
  }
}
